import { useEffect, useRef } from 'react'

import type {
  RealtimeChannel,
  RealtimeStatus,
  ServerFrame,
} from './client'
import { useRealtimeManager } from './context'

/**
 * Subscribes a component to all realtime frames for its lifetime. The handler
 * ref keeps the latest closure without re-registering on every render.
 */
export function useRealtimeFrames(handler: (frame: ServerFrame) => void): void {
  const manager = useRealtimeManager()
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    return manager.addFrameListener((frame) => handlerRef.current(frame))
  }, [manager])
}

export function useRealtimeStatus(
  handler: (status: RealtimeStatus) => void,
): void {
  const manager = useRealtimeManager()
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    return manager.addStatusListener((status) => handlerRef.current(status))
  }, [manager])
}

/** Refetch after a reconnect reaches `ready` again (Pub/Sub is best effort). */
export function useRealtimeResync(handler: () => void): void {
  const manager = useRealtimeManager()
  const handlerRef = useRef(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    return manager.addResyncListener(() => handlerRef.current())
  }, [manager])
}

/**
 * Reference-counted activity/conversation subscription. The manager removes a
 * channel only when the last mounted screen releases it, so StrictMode double
 * mounts and overlapping screens never unsubscribe a live screen.
 */
export function useRealtimeChannel(
  channel: RealtimeChannel | null | undefined,
  id: string | null | undefined,
): void {
  const manager = useRealtimeManager()

  useEffect(() => {
    if (!channel || !id) return
    manager.subscribe(channel, id)
    return () => manager.unsubscribe(channel, id)
  }, [channel, id, manager])
}
